/**
 * Turning a design document into the publish payload.
 *
 * ## Frames are screens
 *
 * A root-level `FRAME` is a **screen**: the thing that gets published and
 * rendered as a page. Everything inside it — charts, shapes, text, nested
 * frames — is that screen's content, with coordinates relative to the frame, so
 * a screen is self-contained and can be rendered without the rest of the
 * document.
 *
 * A root node whose centre is visually inside a frame is treated as content of
 * that frame too. This repairs older/moved designs where a chart looks like it
 * is in the frame but was never structurally re-parented. Remaining root nodes
 * are collected into one implicit screen per page.
 *
 * ## Sample data never ships
 *
 * Widgets dropped from the palette carry generated placeholder data
 * (`widget.sample === true`) so the canvas shows a real chart while designing.
 * That data is stripped here — publishing it would put fake numbers in front of
 * real users.
 */

import type {
  AutoLayout,
  DesignDocument,
  DesignLayer,
  DesignNode,
  DesignPage,
} from '../design-builder.types';
import { buttonConfigOf, opensPopupScreen } from '../../../global-components/dvm-action-button/dvm-action-button.types';
import { roundGeometry } from './design-geometry.utils';
import { inputBoxBindingKeys } from './design-input-data.utils';

/** One publishable screen. */
export interface DesignScreen {
  id: string;
  name: string;
  /** Slug derived from the screen name — the published route segment. */
  slug: string;
  width: number;
  height: number;
  /**
   * The screen fills its container rather than rendering at a fixed `width`.
   * `width` is then only the width it was DESIGNED at — useful for scaling
   * child positions, not for setting the page width.
   */
  fluid: boolean;
  background: string;
  /** Page this screen came from, so multi-page designs stay traceable. */
  pageId: string;
  /** Screen content, with coordinates relative to the screen's own box. */
  nodes: DesignNode[];
  /**
   * The screen's layers, back to front. Metadata only — a node says which layer
   * it is in through its own `layerId`, so nothing is written twice and `nodes`
   * stays the flat, paint-ordered list every renderer already draws.
   */
  layers?: DesignLayer[];
  /** Root-frame layout is part of the screen, not editor chrome. */
  layout?: AutoLayout;
  /** The author set the width / height in the Screen panel: show exactly that, don't trim to the content. */
  fixedWidth?: boolean;
  fixedHeight?: boolean;
  /** Made as a popup screen (its frame's `popupScreen`): opened by a button, never a tab. */
  popupScreen?: boolean;
}

export interface DesignPublishPayload extends Omit<DesignDocument, 'pages'> {
  pages: DesignPage[];
  screens: DesignScreen[];
  /** Convenience for consumers that only care about the count. */
  screenCount: number;
}

// ── Screens ──────────────────────────────────────────────────────────────────

/**
 * Every screen in the document, in page then stacking order.
 *
 * Root frames become screens in their own right. Loose root-level nodes are
 * gathered into a single implicit screen for their page, positioned at the
 * artboard origin.
 */
/**
 * Ids of the screens some button opens as a popup. They are published like
 * any screen (the popup renders them) but left out of the screen tabs.
 */
export function popupScreenIdsOf(document: DesignDocument): Set<string> {
  const ids = new Set<string>();
  const walk = (nodes: readonly DesignNode[]): void => nodes.forEach((node) => {
    // Made as a popup screen: one even before the button that opens it is saved.
    if (node.type === 'FRAME' && node.popupScreen) ids.add(node.id);
    if (node.type === 'WIDGET' && node.widget?.layoutType === 'BUTTON') {
      const config = buttonConfigOf(node.widget.appearance);
      if (opensPopupScreen(config) && config.popupScreenId) ids.add(config.popupScreenId);
    } else if (node.type === 'WIDGET' && node.widget) {
      // Buttons inside table cells (cellConfig.buttons / an action column's cellButtons) can open a popup screen too.
      cellButtonPopupScreenIds(node.widget.appearance, ids);
      cellButtonPopupScreenIds(node.widget.build, ids);
    }
    if (node.children?.length) walk(node.children);
  });
  document.pages.forEach((page) => walk(page.nodes));
  return ids;
}

/** Popup screens opened by table-cell buttons anywhere in a widget's appearance / build. */
function cellButtonPopupScreenIds(value: unknown, ids: Set<string>, depth = 0): void {
  if (!value || typeof value !== 'object' || depth > 8) return;
  if (Array.isArray(value)) {
    value.forEach((item) => cellButtonPopupScreenIds(item, ids, depth + 1));
    return;
  }
  const record = value as Record<string, unknown>;
  // A per-row-value case (actionByValue) running a Button feature carries its config itself.
  if (record['actionKind'] === 'button') {
    const config = buttonConfigOf(record['config'] as Record<string, unknown> | undefined);
    if (opensPopupScreen(config) && config.popupScreenId) ids.add(config.popupScreenId);
  }
  for (const list of [record['buttons'], record['cellButtons']]) {
    if (!Array.isArray(list)) continue;
    (list as Array<Record<string, unknown>>).forEach((button) => {
      const config = buttonConfigOf(button?.['config'] as Record<string, unknown> | undefined);
      if (opensPopupScreen(config) && config.popupScreenId) ids.add(config.popupScreenId);
    });
  }
  Object.values(record).forEach((item) => cellButtonPopupScreenIds(item, ids, depth + 1));
}

/** The screens a viewer can switch between: every screen except popup screens. */
export function pageScreensOf(document: DesignDocument): DesignScreen[] {
  const popups = popupScreenIdsOf(document);
  const screens = screensOf(document);
  const pages = screens.filter((screen) => !popups.has(screen.id));
  return pages.length ? pages : screens;
}

export function screensOf(document: DesignDocument): DesignScreen[] {
  const screens: DesignScreen[] = [];

  for (const page of document.pages) {
    const frames = page.nodes.filter((node) => node.type === 'FRAME');
    const loose = page.nodes.filter((node) => node.type !== 'FRAME');
    const frameOwner = new Map<string, DesignNode>();

    // Match canvas drop targeting: when frames overlap, the later/topmost one
    // owns the visually-contained node.
    for (const node of loose) {
      const centerX = node.x + node.width / 2;
      const centerY = node.y + node.height / 2;
      // A hidden frame (a put-away popup screen) never claims what lies over it.
      const owner = [...frames].reverse().find((frame) => frame.visible !== false &&
        centerX >= frame.x && centerX <= frame.x + frame.width &&
        centerY >= frame.y && centerY <= frame.y + frame.height,
      );
      if (owner) frameOwner.set(node.id, owner);
    }

    for (const frame of frames) {
      const visuallyContained = loose
        .filter((node) => frameOwner.get(node.id)?.id === frame.id)
        .map((node) => ({
          ...node,
          x: roundGeometry(node.x - frame.x),
          y: roundGeometry(node.y - frame.y),
        }));
      screens.push({
        id: frame.id,
        name: frame.name,
        slug: slugifyScreen(frame.name),
        width: roundGeometry(frame.width),
        height: roundGeometry(frame.height),
        fluid: frame.fluid === true,
        ...(frame.screenSize?.width ? { fixedWidth: true } : {}),
        ...(frame.screenSize?.height ? { fixedHeight: true } : {}),
        ...(frame.popupScreen ? { popupScreen: true } : {}),
        background: frameBackground(frame) ?? document.canvas.background,
        pageId: page.id,
        ...(frame.layers?.length ? { layers: frame.layers } : {}),
        layout: frame.layout,
        // Children are already frame-relative. Visually-contained root nodes
        // need rebasing from artboard coordinates into the frame's coordinates.
        nodes: [...(frame.children ?? []), ...visuallyContained],
      });
    }

    const unassigned = loose.filter((node) => !frameOwner.has(node.id));
    if (unassigned.length) {
      screens.push({
        id: `${page.id}_root`,
        name: frames.length ? `${page.name} (loose items)` : page.name,
        slug: slugifyScreen(frames.length ? `${page.name}-loose` : page.name),
        width: document.canvas.width,
        height: document.canvas.height,
        // Loose items were never given a screen width of their own, so they
        // inherit the artboard's and are treated as fluid.
        fluid: true,
        background: document.canvas.background,
        pageId: page.id,
        nodes: unassigned,
      });
    }
  }

  return screens;
}

/** Vertical gap between screens rebuilt onto one artboard. */
const REBUILT_SCREEN_GAP = 80;

/**
 * A published `layout_json` as design-document JSON (`parseDesignDocument`
 * input). The row carries `screens` only — a screen's nodes ARE its frame's
 * children — so each screen is turned back into a root frame, stacked down
 * one page. `screensOf` on the result yields the published screens again.
 * Older rows that still carry `pages` keep them. Either way the design id is
 * restored (see publishedDesignId).
 */
export function publishedLayoutAsDocumentJson(layoutJson: Record<string, unknown>): Record<string, unknown> {
  const id = publishedDesignId(layoutJson);
  const withId = id ? { ...layoutJson, id } : layoutJson;
  if (Array.isArray(layoutJson['pages'])) return withId;
  const screens = (Array.isArray(layoutJson['screens']) ? layoutJson['screens'] : []) as DesignScreen[];
  let top = 0;
  const frames = screens.map((screen) => {
    const frame = {
      id: screen.id,
      type: 'FRAME',
      name: screen.name,
      x: 0,
      y: top,
      width: screen.width,
      height: screen.height,
      ...(screen.fluid ? { fluid: true } : {}),
      // A popup screen stays one in the published view: opened by its button, not a tab.
      ...(screen.popupScreen ? { popupScreen: true } : {}),
      ...(screen.layers?.length ? { layers: screen.layers } : {}),
      ...(screen.layout ? { layout: screen.layout } : {}),
      ...(screen.background ? { style: { fill: { kind: 'solid', color: screen.background } } } : {}),
      children: screen.nodes ?? [],
    };
    top += screen.height + REBUILT_SCREEN_GAP;
    return frame;
  });
  return { ...withId, pages: [{ id: 'page_published', name: 'Page 1', nodes: frames }] };
}

/**
 * The design id behind a published row. Rows published before the id was
 * written carry only `dashboardId` (= name slug + `_` + the id's last 6
 * characters); an id with that suffix keeps publishedDashboardId — and so
 * every republish — on the same row.
 */
export function publishedDesignId(layoutJson: Record<string, unknown>): string | null {
  const stored = layoutJson['id'];
  if (typeof stored === 'string' && stored) return stored;
  const suffix = String(layoutJson['dashboardId'] ?? '').split('_').pop() ?? '';
  return suffix ? `dsg_${suffix}` : null;
}

/**
 * A screen as the published row stores it: each node inside the layer it is
 * painted in, so a layer is one self-contained object and `layerId` is never
 * written. A screen with no layers (an older design) keeps its flat `nodes`.
 */
function storedScreen(screen: DesignScreen): Record<string, unknown> {
  const { nodes, layers, ...rest } = screen;
  if (!layers?.length) return { ...rest, nodes };

  const grouped = new Map(layers.map((layer) => [layer.id, [] as DesignNode[]]));
  const loose: DesignNode[] = [];
  for (const node of nodes) {
    const { layerId, ...stored } = node;
    (grouped.get(layerId ?? '') ?? loose).push(stored);
  }
  return {
    ...rest,
    layers: layers.map((layer) => ({ ...layer, nodes: grouped.get(layer.id) ?? [] })),
    ...(loose.length ? { nodes: loose } : {}),
  };
}

/** A frame's own solid background, when it has one. */
function frameBackground(frame: DesignNode): string | null {
  const fill = frame.style?.fill;
  return fill?.kind === 'solid' && fill.color ? fill.color : null;
}

function slugifyScreen(name: string): string {
  return (
    (name || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'screen'
  );
}

// ── Payload ──────────────────────────────────────────────────────────────────

/**
 * The document as published: sample data stripped, screens resolved.
 * The `pages` tree is kept alongside `screens` so the design can be reopened
 * and edited exactly as it was.
 */
export function buildPublishPayload(document: DesignDocument): DesignPublishPayload {
  const clean: DesignDocument = {
    ...document,
    pages: document.pages.map((page) => ({
      ...page,
      nodes: page.nodes.map(stripSampleData),
    })),
  };

  const screens = screensOf(clean);
  return { ...clean, screens, screenCount: screens.length };
}

/**
 * Row payloads baked into a widget's `[data]` while designing. None of them
 * belong in a published dashboard: sample rows are fiction, and real rows are a
 * snapshot that is stale the moment it is written.
 */
export const WIDGET_ROW_KEYS: readonly string[] = [
  // Row payloads
  'staticData', 'rows', 'data', 'waterfallBars', 'option', 'previewOption',
  // Dropdown choices — these come from the dataset, so they are data too
  'options', 'optionsList', 'optionGroups',
  // Current values of input controls
  'value', 'selected', 'rangeStart', 'rangeEnd', 'uploadedFileNames',
];

/**
 * Strip data from a node and its subtree, keeping only the mapping.
 *
 * `build` stays — it is the tile DEFINITION the published runtime rebuilds
 * from (`tileFromNode` returns null without it), not data — but it is stripped
 * at every depth, so no rows, option lists or picked values ship. The published
 * view fetches those from the dataset on open.
 */
export function stripSampleData(node: DesignNode): DesignNode {
  const next: DesignNode = { ...node };

  if (next.widget) {
    const { sample, build, ...widget } = next.widget as Record<string, unknown> & typeof next.widget;
    const definition = (build ?? {}) as Record<string, unknown>;
    next.widget = {
      ...widget,
      // The mapping lives ONCE, in `build.payload`. `binding` keeps only the
      // summary the canvas and Data tab read, instead of a second full copy.
      ...(build ? { build: stripData(definition) } : {}),
      binding: compactBinding(definition['payload'] as Record<string, unknown> | undefined, widget.binding),
      // A zone slider has no build: its binding IS the mapping (handle, block
      // From / To, record id columns) - compacting it published a slider with
      // its dataset only, drawn from its static numbers.
      ...(widget.layoutType === 'ZONE_SLIDER' && widget.binding
        ? { binding: { ...compactBinding(undefined, widget.binding), ...widget.binding } }
        : {}),
      // An input box's "Fill from data" (dataset / column / aggregation) lives only
      // on its binding: compacting it away published a box that never fetched its
      // value and showed its placeholder.
      ...(widget.layoutType === 'INPUT_BOX' && widget.binding
        ? { binding: { ...compactBinding(definition['payload'] as Record<string, unknown> | undefined, widget.binding), ...inputBoxBindingKeys(widget.binding) } }
        : {}),
      data: stripWidgetRows(widget.data),
    };
  }
  // A bound text published without a componentId can never be computed by the
  // published view — there is nothing to address it by. Texts bound before the
  // id was assigned on bind get a stable one derived from the node id.
  if (next.type === 'TEXT' && next.dataBinding?.dataset && next.dataBinding.field && !next.dataBinding.componentId) {
    next.dataBinding = { ...next.dataBinding, componentId: `CMP-KPI-TEXT-${next.id}` };
  }
  if (next.children?.length) {
    next.children = next.children.map(stripSampleData);
  }
  return next;
}

/**
 * The mapping summary kept on `widget.binding`.
 *
 * The full mapping lives once, in `build.payload`. `binding` used to be a
 * second copy of it, which is how a dropdown ended up storing the same config
 * three times over; these are the only keys anything reads off it.
 */
const BINDING_KEYS = ['dataset', 'datasets', 'field', 'aggregation', 'rowsKey', 'childrenField'];

export function compactBinding(
  payload: Record<string, unknown> | undefined,
  binding: Record<string, unknown> | undefined,
): Record<string, unknown> {
  const source = { ...(payload ?? {}), ...(binding ?? {}) };
  const out: Record<string, unknown> = {};
  BINDING_KEYS.forEach((key) => {
    if (source[key] !== undefined) out[key] = source[key];
  });
  return out;
}

/** Drop every data-bearing key, including from nested config objects. */
function stripData(source: Record<string, unknown>): Record<string, unknown> {
  const clean: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(source)) {
    if (WIDGET_ROW_KEYS.includes(key)) continue;
    clean[key] = isPlainObject(value) ? stripData(value) : value;
  }
  return clean;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function stripWidgetRows(data: Record<string, unknown> | null): Record<string, unknown> | null {
  if (!data) return null;
  const clean = stripData(data);
  return Object.keys(clean).length ? clean : null;
}

/**
 * Widgets that always show live compute-API rows: their rows are never saved
 * with the design nor shown from it (a stored copy is stale the moment it is
 * written). Sample widgets keep their sample - they have no API to call.
 */
const LIVE_ONLY_LAYOUTS: ReadonlySet<string> = new Set(['NESTED_TABLE']);

export function isLiveOnlyWidget(widget: { layoutType?: string; sample?: boolean } | null | undefined): boolean {
  return !!widget && !widget.sample && LIVE_ONLY_LAYOUTS.has(widget.layoutType ?? '');
}

/**
 * A dropdown widget as saved: no option list (in its build's `dropdownControl`
 * or its data). Options always come from the compute API on open.
 */
export function withoutSavedDropdownOptions<W extends { layoutType?: string; build?: Record<string, unknown> | null; data?: Record<string, unknown> | null }>(widget: W): W {
  if (widget.layoutType !== 'DROPDOWN') return widget;
  const control = widget.build?.['dropdownControl'];
  const build = control && typeof control === 'object'
    ? { ...widget.build, dropdownControl: { ...(control as Record<string, unknown>), options: [] } }
    : widget.build;
  const data = widget.data && 'options' in widget.data ? { ...widget.data, options: [] } : widget.data;
  return { ...widget, build, data };
}

/** A widget's data without any rows: what a live-only widget keeps / shows until the compute API answers. */
export function withoutStoredRows(data: Record<string, unknown> | null | undefined): Record<string, unknown> | null {
  return stripWidgetRows(data ?? null);
}

/** True when any widget in the document is still showing sample data — the
 *  shell warns before publishing so nobody ships an unbound chart by accident. */
export function hasSampleData(document: DesignDocument): boolean {
  const walk = (nodes: readonly DesignNode[]): boolean =>
    nodes.some(
      (node) => node.widget?.sample === true || (node.children?.length ? walk(node.children) : false),
    );
  return document.pages.some((page) => walk(page.nodes));
}

/**
 * The `layout_json` written to the platform `dashboard_layouts` table.
 *
 * Design-native on purpose. Flattening a free-positioned screen into the Layout
 * Builder's 12-column grid would throw away the overlaps, shapes, text and
 * icons that are the reason to design one — so the screens go across intact and
 * `builder: 'design'` tells the published view to render them with the screen
 * renderer rather than the grid one.
 */
export function buildPublishedLayoutJson(document: DesignDocument): Record<string, unknown> {
  const payload = buildPublishPayload(document);
  const now = new Date().toISOString();

  return {
    'dashboard-name': document.name,
    name: document.name,
    // The design's own id: reopening from the published row keeps it, so a
    // republish lands on the same dashboard_id.
    id: document.id,
    dashboardId: publishedDashboardId(document),
    builder: 'design',
    schemaVersion: payload.schemaVersion,
    status: 'published',
    publishedAt: now,
    updatedAt: now,
    canvas: payload.canvas,
    screens: payload.screens.map(storedScreen),
    screenCount: payload.screenCount,
    // `pages` is deliberately NOT sent. A screen's nodes ARE the frame's
    // children, so shipping both wrote every node twice. The editable tree
    // lives in the template store; this row only has to RENDER.
    ...(document.projectId ? { projectId: document.projectId, projectName: document.projectName } : {}),
  };
}

/**
 * The published dashboard's id: a readable underscore slug of the name plus a
 * short suffix from the design id.
 *
 * The name alone is not an id — two dashboards can share one, renaming one
 * would orphan its published row, and a name with spaces or punctuation makes
 * a poor key and a worse URL. The suffix keeps it unique and stable across
 * renames; the slug keeps it legible.
 */
export function publishedDashboardId(document: DesignDocument): string {
  const slug =
    (document.name || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 60) || 'dashboard';

  const suffix = (document.id || '').replace(/[^a-z0-9]+/gi, '').slice(-6).toLowerCase();
  return suffix ? `${slug}_${suffix}` : slug;
}
